import {
  createContext,
  FC,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useCompanyStore } from "../stores/useCompanyStore";
import { useUserContext } from "./UserContextProvider";
import { usePoliciesContext } from "./PoliciesContextProvider";

export interface TrackDeskConfig {
  linkId: string | null;
  sourceId: string | null;
  tenantId: string | null;
  clickId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  countryCode: string | null;
}

interface IAffiliateSignupsContext {
  trackdeskConfig: TrackDeskConfig | null;
  removeTrackdeskConfigFromLocalStorage: () => void;
}

const AffiliateSignupsContext = createContext<IAffiliateSignupsContext>({
  trackdeskConfig: null,
  removeTrackdeskConfigFromLocalStorage: () => null,
});

// Help functions
export const useAffiliateSignupsContext = () => {
  return useContext(AffiliateSignupsContext);
};

const getTrackDeskConfigFromParams = (
  searchParams: URLSearchParams
): TrackDeskConfig => {
  const keys = ["linkId", "sourceId", "tenantId", "clickId"];
  const config: Partial<TrackDeskConfig> = {};

  keys.forEach((key) => {
    config[key as keyof TrackDeskConfig] = searchParams.get(key);
  });

  return {
    ...config,
    ipAddress: null,
    userAgent: navigator.userAgent,
    countryCode: null,
  } as TrackDeskConfig;
};

const saveTrackDeskConfigToLocalStorage = (config: TrackDeskConfig) => {
  localStorage.setItem(
    "trackdeskConfig",
    new URLSearchParams(config as any).toString()
  );
};

const loadTrackDeskConfigFromLocalStorage = (): TrackDeskConfig | null => {
  const configString = localStorage.getItem("trackdeskConfig");
  if (!configString) return null;

  const parsedQueryString = new URLSearchParams(configString);
  const config = Object.fromEntries(parsedQueryString.entries());

  return {
    ...config,
    userAgent: navigator.userAgent,
  } as TrackDeskConfig;
};

const removeParamsFromSearchParams = (
  searchParams: URLSearchParams,
  keys: string[]
) => {
  keys.forEach((key) => {
    searchParams.delete(key);
  });
  return searchParams;
};

export const AffiliateSignupsContextProvider: FC<{
  children: ReactElement;
}> = ({ children }) => {
  const { clearUser } = useUserContext();
  const { company, clearCompany } = useCompanyStore();
  const { clearPolicies } = usePoliciesContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [trackdeskConfig, setTrackdeskConfig] =
    useState<TrackDeskConfig | null>(null);

  const removeTrackdeskConfigFromLocalStorage = () => {
    localStorage.removeItem("trackdeskConfig");
    setTrackdeskConfig(null);
  };

  useEffect(() => {
    const config = getTrackDeskConfigFromParams(searchParams);
    const hasConfig =
      config.linkId && config.sourceId && config.tenantId && config.clickId;

    if (hasConfig) {
      window.localStorage.clear();
      clearUser();
      clearCompany();
      clearPolicies();
      saveTrackDeskConfigToLocalStorage(config);
      const newSearchParams = removeParamsFromSearchParams(searchParams, [
        "linkId",
        "sourceId",
        "tenantId",
        "clickId",
      ]);
      setSearchParams(newSearchParams);
    } else {
      const storedConfig = loadTrackDeskConfigFromLocalStorage();
      if (storedConfig) {
        setTrackdeskConfig(storedConfig);
      }
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (company?.currentSubscription?.details?.active) {
      removeTrackdeskConfigFromLocalStorage();
    }
  }, [company?.currentSubscription?.details?.active]);

  return (
    <AffiliateSignupsContext.Provider
      value={{
        trackdeskConfig,
        removeTrackdeskConfigFromLocalStorage,
      }}
    >
      {children}
    </AffiliateSignupsContext.Provider>
  );
};
